import React, {ReactElement} from "react";
import {
  FORMGEN_STYLE_FLAG_NO_CONTAINER,
  FormGenAgent,
  FormGenMetadata,
  formGenMetadataKey,
  FormGenSetValueResult
} from "./formgen";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Switch,
  TextareaAutosize,
  TextField,
  Typography
} from "@mui/material";
import {
  StyledBoxColumn,
  StyledBoxColumnWithLabel,
  StyledBoxRow,
  StyledHorizontalStack,
  StyledMarkdown,
  StyledSpan,
  StyledVerticalStack
} from "./StyledComponents";
import {
  DIVIDER,
  DW_SM,
  DW_XS,
  PAGE_FRAGMENT_HALF_WIDTH,
  PAGE_FRAGMENT_WIDTH,
  PD_MD,
  PD_SM,
  PD_XLG,
  PD_XSM,
  PD_XXSM,
  SZ_JUMBO,
  SZ_LG,
  SZ_SM,
  SZ_XXLG
} from "./dimens";
import {DesktopDatePicker, DesktopDateTimePicker, DesktopTimePicker} from "@mui/x-date-pickers";
import {
  PROFILE_PHOTO_STYLE_JUMBO,
  PROFILE_PHOTO_STYLE_MD,
  PROFILE_PHOTO_STYLE_XXLG,
  ProfilePhotoStyle
} from "./constants";
import {$KTS, Action, KeyTextString, Presets, PresetsKey, UserProfilePhoto} from "./types";
import {ActionsDialogContent} from "./Dialogs";
import {FormGenAutocomplete} from "./FormGenAutocomplete";
import {BaseApp} from "./BaseApp";
import {FormGenIconSelect} from "./FormGenIconSelect";
import {validateEmail} from "./text_util";
import {colorRed, lightGray, mediumGray} from "./colors";
import {StoredDataFileUploadFragment} from "./StoredDateFileUploadFragment";
import {FormGenColorSelect} from "./FormGenColorSelect";
import {FormGenLatLng} from "./FormGenLatLng";
import {
  AutoAwesomeOutlined,
  ContentCopyOutlined,
  ImageOutlined,
  MicOutlined,
  RestartAltOutlined,
  UploadFileOutlined
} from "@mui/icons-material";
import dayjs from "dayjs";
import {FormGenCountrySelect} from "./FormGenCountrySelect";
import {md5, md5_uuid} from "./md5";
import {PathProps} from "./index";
import {FormGenRichText} from "./FormGenRichText";
import {MimeTypeMap} from "./mime_types";
import iconMimeTypeApplicationZip from "./res/icons/mimetypes/application_zip.svg";
import {RecordAudioFragment} from "./RecordAudioFragment";
import {StringUtil} from "./string_util";
import {FormGenDateRange} from "./FormGenDateRange";
import {FormGenPasskey} from "./FormGenPasskey";
import {FormGenChecklist} from "./FormGenChecklist";
import {FormGenArrayString} from "./FormGenArrayString";
import Checkbox from "@mui/material/Checkbox";
import {FormGenArrayObject} from "./FormGenArrayObject";
import {FormGenProfilePhotoOptionsDialog} from "./FormGenProfilePhotoOptionsDialog";
import {FormGenTextAutoEnhance} from "./FormGenTextAutoEnhance";
import {FileUtil} from "./file_util";

export enum FormGenContainerMode {
  READ,
  CREATE,
  EDIT,
}

export type FormGenContainerProps = {
  path?: PathProps,
  style?: any,
  mode?: FormGenContainerMode,
  title?: string,
  disabled?: boolean,
  description?: string,
  content: any,
  renderOptions?: {
    showContentId?: boolean,
  },
  multiStep?: boolean,
  multiStepIndex?: number,
  showPropertyIds?: string[], // Only show properties (including for nested objects) with ids.
  hidePropertyIds?: string[], // Hide properties (including for nested objects) with ids.
  nestedObjectId?: string, // If this is set, only the nested object with id is shown.
  onContainerDidValidate?: (container: FormGenContainer, success: boolean) => void,
  layout?: "default" | "compact" | "custom",
  customLayout?: (path: PathProps, source: FormGenLayoutSource) => AbstractFormGenLayout, // Used when layout === "custom"
  disableAutoWrite?: boolean,
  autoSave?: boolean, // If true, will call onContainerSave() automatically a short duration (or saveDelay, if set) after any change is made.
  saveDelay?: number,
  onContainerRequiredItemsFilledChanged?: (container: FormGenContainer, filled: boolean) => void, // Called after all required items are filled, or when any required item becomes unfilled after all items are filled.
  // Convenience method for Fragments that enable/disable action button based on filled state of required items.
  // The action button click will also be captured (via setActionButtonOnClick, unless overridden in the Fragment sub-class) to invoke onContainerSave() on click.
  saveActionButtonProvider?: {
    setActionButtonEnabled: (enabled: boolean) => void,
    setActionButtonOnClick: (((event) => void) | null | undefined),
  },
  onContainerWillSave?: (container: FormGenContainer) => void, // Always called after a change is made.
  onContainerSave?: (container: FormGenContainer, callback?: (success: boolean) => void) => void, // hasUnsavedChanges state is set to false when callback returns true
  showSaveButton?: boolean, // If true, shows a "Save" button which calls onContainerSave when clicked, only in Editing mode and default layout.
  footer?: ReactElement,
}

export type FormGenContainerState = {
  hasUnsavedChanges?: boolean,
  hasUnfilledRequiredItems?: boolean,
  contentItems?: FormGenContentItem[],
  _values: any,
  _hiddens?: any,
  _errors?: any,
}

export type FormGenContentItem = {
  id: string,
  target: any,
  metadata: FormGenMetadata,
  hidden: boolean,
  // By default this is true, but nested objects derive this from their parent chain.
  // Nested object fields should derive their required behavior from this field + their own metadata.
  parentRequired?: boolean,
}

export type FormGenNestedObjectItem = {
  id: string,
  propertyKey: string,
  text: string,
}

export function formGenContentItemTransformText(contentItem: FormGenContentItem, str: string) {
  if (!contentItem.metadata.transformText) {
    return str;
  }
  if (contentItem.metadata.transformText === "uppercase") {
    return str.toUpperCase();
  } else if (contentItem.metadata.transformText === "lowercase") {
    return str.toUpperCase();
  } else {
    return contentItem.metadata.transformText(str);
  }
}

export interface FormGenLayoutSource {

  getKey(): string;

  getMode(): FormGenContainerMode;

  getValue(contentItem: FormGenContentItem);

  setValue(contentItem: FormGenContentItem, value: any);

  getError(contentItem: FormGenContentItem): string | undefined | null;

  setError(contentItem: FormGenContentItem, error: string | undefined | null);
}

export enum FormGenLayoutType {
  COMPACT = "compact",
  DEFAULT = "default",
  READ = "read",
}

export type FormGenLayoutOptions = {
  hideLabel?: boolean,
}

export interface FormGenLayout {
  renderInContainer(contentItem: FormGenContentItem, children: ReactElement, options?: FormGenLayoutOptions): ReactElement;
}

abstract class AbstractFormGenLayout implements FormGenLayout {

  constructor(readonly path: PathProps, readonly type: FormGenLayoutType | string, readonly source: FormGenLayoutSource) {
  }

  isReadonly(contentItem: FormGenContentItem): boolean {
    return (this.source.getMode() === FormGenContainerMode.EDIT && contentItem.metadata.noneditable)
      || contentItem.metadata.readonly
      || contentItem.metadata.readonlyFn?.(contentItem.target);
  }

  abstract renderBoolean(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderString(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderText(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderRichText(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderRichContent(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderCurrency(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderCopyable(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderEmail(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderPasskey(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderNumber(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderEnum(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderChecklist(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderDate(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderTime(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderDateTime(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderDatenum(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderTimenum(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderDateTimenum(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderDateRange(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderObject(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderCustom(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderProfilePhoto(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderIcon(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderColor(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderAutocomplete(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderCountrySelect(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderFile(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderArrayString(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderArrayAutocomplete(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderArrayFile(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderArrayObject(contentItem: FormGenContentItem, index: number): ReactElement;

  abstract renderLatLng(contentItem: FormGenContentItem, index: number): ReactElement;

  renderInContainer(contentItem: FormGenContentItem, children: ReactElement, options?: FormGenLayoutOptions): ReactElement {
    return children;
  }
}

abstract class BaseFormGenLayout extends AbstractFormGenLayout {
  renderInContainer(contentItem: FormGenContentItem, children: ReactElement, options?: FormGenLayoutOptions) {
    let slugText: string;
    if (contentItem.metadata.type === "string" && contentItem.metadata.slug) {
      const value = this.source.getValue(contentItem) as string;
      slugText = `${(contentItem.metadata.slugLabel || "") + (value ? StringUtil.toSlug(value) : "")}`;
    }
    let description: string;
    if (contentItem.metadata.type === "enum") {
      description = contentItem.metadata.enumDescriptions?.find(keyText => keyText.key === this.source.getValue(contentItem))?.text;
    }
    if (!description) {
      description = contentItem.metadata.description;
    }
    const error = this.source.getError(contentItem);
    const showLabel = !options?.hideLabel && contentItem.metadata.type !== "object";
    return <StyledVerticalStack
      label={showLabel ? contentItem.metadata.name : null}
      required={showLabel && contentItem.metadata.required}
      tooltip={contentItem.metadata.tooltip}
      style={{maxWidth: PAGE_FRAGMENT_WIDTH, marginLeft: "auto", marginRight: "auto"}}>
      {children}
      {slugText ? <Typography variant="body2" style={{marginTop: PD_XXSM}}>{slugText}</Typography> : null}
      {description ? <Typography variant="body2" style={{marginTop: PD_XXSM}}>{description}</Typography> : null}
      {error ? <Typography variant="body2" style={{color: colorRed}}>{error}</Typography> : null}
    </StyledVerticalStack>;
  }
}

export class BaseCustomFormGenLayout extends AbstractFormGenLayout {

  constructor(path: PathProps, type: string, source: FormGenLayoutSource) {
    super(path, type, source);
  }

  isReadonly(contentItem: FormGenContentItem): boolean {
    return false;
  }

  renderArrayAutocomplete(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderArrayFile(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderArrayString(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderArrayObject(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderAutocomplete(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderBoolean(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderColor(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderCopyable(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderCountrySelect(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderCustom(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderDate(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderDateTime(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderDateTimenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderDatenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderDateRange(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderEmail(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderPasskey(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderEnum(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderChecklist(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderFile(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderIcon(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderNumber(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderObject(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderProfilePhoto(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderRichText(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderRichContent(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderCurrency(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderString(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderText(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderTime(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderTimenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return undefined;
  }

  renderLatLng(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }
}

class ReadFormGenLayout extends BaseFormGenLayout {

  constructor(path: PathProps, source: FormGenLayoutSource) {
    super(path, FormGenLayoutType.READ, source);
  }

  renderBoolean(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{Boolean(this.source.getValue(contentItem)) ? "True" : "False"}</Typography>
    );
  }

  renderString(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{this.source.getValue(contentItem) || "-"}</Typography>
    );
  }

  renderText(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{this.source.getValue(contentItem)}</Typography>
    );
  }

  renderRichText(contentItem: FormGenContentItem, index: number) {
    const markdownText = this.source.getValue(contentItem);
    if (!(markdownText?.length > 0)) {
      return <Typography>-</Typography>;
    }
    return this.renderInContainer(contentItem,
      <StyledMarkdown>
        {markdownText}
      </StyledMarkdown>
    );
  }

  renderRichContent(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <div dangerouslySetInnerHTML={this.source.getValue(contentItem)}/>
    );
  }

  renderCurrency(contentItem: FormGenContentItem, index: number) {
    const currency: number = Math.floor(this.source.getValue(contentItem) || 0) / 100;
    return this.renderInContainer(contentItem,
      <Typography>{`${contentItem.metadata.currencyType.prefix} ${currency.toFixed(2)}`}</Typography>
    );
  }

  renderCopyable(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{this.source.getValue(contentItem)}</Typography>
    );
  }

  renderEmail(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{this.source.getValue(contentItem)}</Typography>
    );
  }

  renderPasskey(contentItem: FormGenContentItem, index: number) {
    const value = this.source.getValue(contentItem) as string;
    return this.renderInContainer(contentItem,
      <Typography>{new Array(value.length).fill("•")}</Typography>
    );
  }

  renderNumber(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{this.source.getValue(contentItem)}</Typography>
    );
  }

  renderEnum(contentItem: FormGenContentItem, index: number) {
    let text;
    if (contentItem.metadata.enumMulti) {
      const values = (this.source.getValue(contentItem) as string[]) || [];
      text = values.map(value => contentItem.metadata.enumValues.find(val => val.key === value)?.text).filter(Boolean).join(", ");
    } else {
      text = contentItem.metadata.enumValues.find(val => val.key === this.source.getValue(contentItem))?.text || "-";
    }
    return this.renderInContainer(contentItem,
      <Typography>{text}</Typography>
    );
  }

  renderChecklist(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderDate(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{this.source.getValue(contentItem)}</Typography>
    );
  }

  renderTime(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{this.source.getValue(contentItem)}</Typography>
    );
  }

  renderDateTime(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography>{this.source.getValue(contentItem)}</Typography>
    );
  }

  renderDatenum(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderTimenum(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderDateTimenum(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderDateRange(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderObject(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      <Typography variant="h6" style={{marginTop: PD_SM}}>{contentItem.metadata.name}</Typography>,
      {hideLabel: true}
    );
  }

  renderCustom(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      contentItem.metadata.customRenderer?.(contentItem.target, this.source.getValue(contentItem), value => this.source.setValue(contentItem, value), {
        path: this.path,
        mode: FormGenContainerMode.READ
      }),
    );
  }

  renderProfilePhoto(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, <Box style={{display: "flex", alignItems: "flex-start"}}>
      <Box style={{display: "flex", flexDirection: "column", alignItems: "center", gap: PD_XSM}}>
        <Card>
          <img
            alt={contentItem.metadata.name}
            style={{
              ...ProfilePhotoStyle(contentItem.metadata.imageOptions?.keepAspectRatio),
              verticalAlign: "top",
              objectFit: contentItem.metadata.profilePhotoObjectFit || "cover"
            }}
            src={this.source.getValue(contentItem) || contentItem.metadata.profilePhotoDefaultImageUrl || UserProfilePhoto(null)}/>
        </Card>
      </Box>
    </Box>);
  }

  renderIcon(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderColor(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderAutocomplete(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderCountrySelect(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderFile(contentItem: FormGenContentItem, index: number) {
    const url = this.source.getValue(contentItem);
    return this.renderInContainer(contentItem,
      <Card style={{
        ...PROFILE_PHOTO_STYLE_MD,
        backgroundColor: lightGray,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        {url
          ? <img src={url} style={{width: "100%", height: "100%", objectFit: "contain"}}/>
          : <ImageOutlined style={{width: "50%", height: "50%", color: mediumGray, opacity: 0.5}}/>}
      </Card>
    );
  }

  renderArrayString(contentItem: FormGenContentItem, index: number) {
    const arrayString: string[] = this.source.getValue(contentItem);
    return this.renderInContainer(contentItem,
      arrayString?.length > 0
        ? <StyledBoxColumn>
          {arrayString?.map(item => <Typography>{item}</Typography>)}
        </StyledBoxColumn>
        : <Typography>-</Typography>
    );
  }

  renderArrayAutocomplete(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderArrayFile(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderArrayObject(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderLatLng(contentItem: FormGenContentItem, index: number): React.ReactElement {
    const arrayNum: number[] = this.source.getValue(contentItem);
    return this.renderInContainer(contentItem,
      arrayNum?.length === 2
        ? <StyledBoxRow>
          <StyledBoxColumnWithLabel style={{flexBasis: 0, flexGrow: 1}} label={"Latitude"}>
            <Typography>{arrayNum[0]}</Typography>
          </StyledBoxColumnWithLabel>
          <StyledBoxColumnWithLabel style={{flexBasis: 0, flexGrow: 1}} label={"Longitude"}>
            <Typography>{arrayNum[1]}</Typography>
          </StyledBoxColumnWithLabel>
        </StyledBoxRow>
        : <Typography>-</Typography>
    );
  }
}

class DefaultFormGenLayout extends BaseFormGenLayout {

  constructor(path: PathProps, source: FormGenLayoutSource) {
    super(path, FormGenLayoutType.DEFAULT, source);
  }

  renderBoolean(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <Switch
        disabled={this.isReadonly(contentItem)}
        style={{flexGrow: 1}}
        checked={this.source.getValue(contentItem)}
        value={this.source.getValue(contentItem)}
        onChange={event => this.source.setValue(contentItem, event.target.checked)}
        id={contentItem.id}
      />
    ));
  }

  renderString(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <TextField
          inputMode={contentItem.metadata.inputMode}
          inputProps={{
            maxLength: contentItem.metadata.maxLength,
            inputMode: contentItem.metadata.inputMode,
          }}
          size="small"
          multiline={contentItem.metadata.multilineStringRows > 1}
          rows={contentItem.metadata.multilineStringRows}
          autoFocus={index === 0 || contentItem.metadata.autoFocus}
          disabled={this.isReadonly(contentItem)}
          style={{width: SZ_LG, flexGrow: 1}}
          value={this.source.getValue(contentItem) || ""}
          onChange={event => this.source.setValue(contentItem, formGenContentItemTransformText(contentItem, event.target.value))}
          id={contentItem.id}
          variant="outlined"
        />
      </Box>
    ));
  }

  private textContentHeight(contentItem: FormGenContentItem) {
    switch (contentItem.metadata.size) {
      case "xsmall":
        return SZ_LG;
      case "small":
        return SZ_XXLG;
      case "large":
        return SZ_JUMBO * 1.5;
    }
    return SZ_JUMBO;
  }

  renderText(contentItem: FormGenContentItem, index: number): ReactElement {
    const value = this.source.getValue(contentItem) || "";
    return this.renderInContainer(contentItem, (
      <StyledBoxColumn>
        <TextareaAutosize
          placeholder={contentItem.metadata.placeholder}
          disabled={this.isReadonly(contentItem)}
          style={{
            height: this.textContentHeight(contentItem),
            flexGrow: 1,
            resize: "vertical",
            overflow: "auto",
            fontFamily: "sans-serif",
            fontSize: "100%",
            padding: PD_SM
          }}
          value={value}
          onChange={event => this.source.setValue(contentItem, event.target.value)}
          id={contentItem.id}
        />
        {FormGenContainer.hasAgent() && contentItem.metadata.textAutoEnhancePrompt
          ? <StyledBoxRow>
            <StyledSpan/>
            <Button
              disabled={!value}
              onClick={() => {
                const id = BaseApp.CONTEXT.showDialog(null, () => <StyledBoxColumn
                  style={{width: "92dvw", maxWidth: DW_SM}}>
                  <FormGenTextAutoEnhance
                    key={contentItem.id}
                    agent={FormGenContainer.getAgent()}
                    prompt={contentItem.metadata.textAutoEnhancePrompt} text={value}
                    onResponse={response => {
                      BaseApp.CONTEXT.hideDialog(id);
                      this.source.setValue(contentItem, response);
                    }}/>
                </StyledBoxColumn>);
              }}
              startIcon={<AutoAwesomeOutlined/>}>Enhance</Button>
          </StyledBoxRow>
          : null}
      </StyledBoxColumn>
    ));
  }

  renderRichText(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <FormGenRichText
        placeholder={contentItem.metadata.placeholder}
        disabled={this.isReadonly(contentItem)}
        contentHeight={this.textContentHeight(contentItem)}
        contentItem={contentItem}
        index={index}
        source={this.source}/>
    ));
  }

  renderRichContent(contentItem: FormGenContentItem, index: number) {
    const FormGenRichContentEditorType = contentItem.metadata.richContentType;
    if (!Boolean(FormGenRichContentEditorType)) {
      return this.renderText(contentItem, index);
    }
    return this.renderInContainer(contentItem,
      <FormGenRichContentEditorType/>
    );
  }

  private _currencyLastKeyNum: number;

  renderCurrency(contentItem: FormGenContentItem, index: number) {
    const value = this.source.getValue(contentItem) || 0;
    return this.renderInContainer(contentItem, (
      <Box display="flex" flexDirection="row" style={{gap: PD_SM, alignItems: "center"}}>
        <Typography variant="h6">{contentItem.metadata.currencyType.prefix}</Typography>
        <TextField
          inputMode="numeric"
          inputProps={{maxLength: contentItem.metadata.maxLength}}
          size="small"
          autoFocus={index === 0 || contentItem.metadata.autoFocus}
          disabled={this.isReadonly(contentItem)}
          style={{width: SZ_LG, flexGrow: 1}}
          value={(value / 100).toFixed(2)}
          onKeyDown={event => {
            if (event.keyCode === 8) { // Backspace
              this._currencyLastKeyNum = -1;
            }
            const n = Number(event.key);
            if (!Number.isInteger(n)) {
              return;
            }
            this._currencyLastKeyNum = n;
          }}
          onChange={event => {
            let newValue = Math.floor(Number(event.target.value) * 100);
            if (Math.floor(newValue / 100) === Math.floor(value / 100) && this._currencyLastKeyNum === -1) {
              newValue = value / 10;
            } else {
              if (newValue === value && this._currencyLastKeyNum >= 0 && this._currencyLastKeyNum <= 9) {
                newValue = value * 10 + this._currencyLastKeyNum;
              }
            }
            this.source.setValue(contentItem, Math.floor(newValue));
          }}
          id={contentItem.id} variant="outlined"
        />
      </Box>
    ));
  }

  renderCopyable(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <Box display="flex" flexDirection="row">
        <Typography
          id={contentItem.id}>
          {this.source.getValue(contentItem)}
          <Button style={{marginLeft: PD_SM}}>
            <ContentCopyOutlined/>
          </Button>
        </Typography>
      </Box>
    ));
  }

  renderEmail(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <TextField
          size="small"
          disabled={this.isReadonly(contentItem)}
          style={{width: SZ_LG, flexGrow: 1}}
          value={this.source.getValue(contentItem)}
          onChange={event => this.source.setValue(contentItem, event.target.value)}
          onBlur={() => {
            const error = !validateEmail(this.source.getValue(contentItem)) ? "Please enter a valid email" : null;
            this.source.setError(contentItem, error);
          }}
          id={contentItem.id} variant="outlined"
          required/>
      </Box>
    ));
  }

  renderPasskey(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <FormGenPasskey contentItem={contentItem} index={index} source={this.source}/>
    ));
  }

  renderNumber(contentItem: FormGenContentItem, index: number): ReactElement {
    switch (contentItem.metadata.numberVariant) {
      case "slider":
        return this.renderNumberSlider(contentItem);
      case "spinner":
      default:
        return this.renderNumberSpinner(contentItem, index);
    }
  }

  private renderNumberSpinner(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <TextField
        inputProps={{maxLength: contentItem.metadata.maxLength}}
        size="small"
        type="number"
        multiline={contentItem.metadata.multilineStringRows > 1}
        rows={contentItem.metadata.multilineStringRows}
        autoFocus={index === 0}
        disabled={this.isReadonly(contentItem)}
        style={{width: SZ_LG, flexGrow: 1}}
        value={this.source.getValue(contentItem)}
        onChange={event => this.source.setValue(contentItem, Number.parseFloat(event.target.value))}
        id={contentItem.id} variant="outlined" required/>
    ));
  }

  private renderNumberSlider(contentItem: FormGenContentItem) {
    let min = contentItem.metadata.numberMin || 0;
    let max = contentItem.metadata.numberMax || 100;
    let step = contentItem.metadata.numberStep || 1;
    let parseFn: (x: number) => number = (x) => x;
    const intOnly = contentItem.metadata.numberIntegerOnly;
    if (intOnly) {
      parseFn = Math.floor;
    }
    const value: number = this.source.getValue(contentItem);
    return this.renderInContainer(contentItem, (
      <StyledBoxRow style={{alignItems: "center", height: SZ_SM, gap: PD_XLG}}>
        <Slider
          value={value}
          onChange={(event, value: number) => this.source.setValue(contentItem, parseFn(value))}
          valueLabelDisplay="auto"
          step={parseFn(step)}
          marks
          min={parseFn(min)}
          max={parseFn(max)}
        />
        <Typography variant="h6">{value.toFixed(intOnly ? 0 : 2)}</Typography>
      </StyledBoxRow>
    ));
  }

  renderEnum(contentItem: FormGenContentItem, index: number): ReactElement {
    switch (contentItem.metadata.enumVariant) {
      case "button":
        return this.renderEnumButton(contentItem);
      case "radio":
        return this.renderEnumRadio(contentItem);
      case "select":
      default:
        return this.renderEnumSelect(contentItem);
    }
  }

  private renderEnumButton(contentItem: FormGenContentItem) {
    const {selectedValue, enumValues} = this.readEnumValue(contentItem);
    return this.renderInContainer(contentItem, (
      <Box display="flex" flexDirection="row" flexWrap="wrap" style={{gap: PD_SM}}>
        {enumValues?.map(value => {
          const selected = contentItem.metadata.enumMulti ? (selectedValue as string[])?.includes(value.key) : selectedValue === value.key;
          return <Button
            onClick={() => {
              if (contentItem.metadata.enumMulti) {
                const values = selectedValue as string[];
                if (selected) {
                  const valueIndex = values.indexOf(value.key);
                  this.source.setValue(contentItem, this.maybeSortEnumMultiValues(contentItem, enumValues, [...values.slice(0, valueIndex), ...values.slice(valueIndex + 1)]));
                } else {
                  this.source.setValue(contentItem, this.maybeSortEnumMultiValues(contentItem, enumValues, [...(values || []), value.key]));
                }
              } else {
                this.source.setValue(contentItem, value.key);
              }
            }}
            variant={(selected ? "contained" : "outlined")}>
            {value.text}
          </Button>;
        })}
      </Box>
    ));
  }

  private renderEnumRadio(contentItem: FormGenContentItem) {
    let selectedValue = this.source.getValue(contentItem);
    if (contentItem.metadata.enumMulti && (selectedValue === undefined || selectedValue === null)) {
      this.source.setValue(contentItem, []);
      selectedValue = [];
    }
    if (!selectedValue && contentItem.metadata.enumDefaultKey) {
      this.source.setValue(contentItem, contentItem.metadata.enumDefaultKey);
      selectedValue = contentItem.metadata.enumDefaultKey;
    }
    return this.renderInContainer(contentItem,
      contentItem.metadata.enumMulti
        ? <StyledBoxColumn style={{gap: 0}}>
          {contentItem.metadata.enumValues?.map(value => {
            const values = selectedValue as string[];
            const valueIndex = values.indexOf(value.key);
            return <FormControlLabel
              label={value.text}
              value={value.key}
              control={<Checkbox
                onChange={(event, checked) => {
                  if (checked && valueIndex < 0) {
                    this.source.setValue(contentItem, [...values, value.key]);
                  } else if (!checked && valueIndex >= 0) {
                    this.source.setValue(contentItem, [...values.slice(0, valueIndex), ...values.slice(valueIndex + 1)]);
                  }
                }}
                checked={valueIndex >= 0}/>}/>;
          })}
        </StyledBoxColumn>
        : <RadioGroup
          value={selectedValue}
          onChange={event => this.source.setValue(contentItem, event.target.value)}
        >
          {contentItem.metadata.enumValues?.map(value => {
            return <FormControlLabel
              label={value.text}
              value={value.key}
              control={<Radio/>}/>;
          })}
        </RadioGroup>
    );
  }

  private renderEnumSelect(contentItem: FormGenContentItem) {
    const {selectedValue, enumValues} = this.readEnumValue(contentItem);
    return this.renderInContainer(contentItem, (
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <FormControl variant="outlined">
          <Select
            variant={"outlined"}
            MenuProps={{
              style: {zIndex: 10000}
            }}
            id={contentItem.id}
            multiple={contentItem.metadata.enumMulti}
            size="small"
            sx={{width: DW_XS}}
            disabled={this.isReadonly(contentItem)}
            value={selectedValue}
            onChange={event => {
              let value = event.target.value;
              if (contentItem.metadata.enumMulti) {
                value = this.maybeSortEnumMultiValues(contentItem, enumValues, value);
              }
              this.source.setValue(contentItem, value);
            }}>
            {enumValues.map(value => {
              return <MenuItem value={value.key}>
                {value.text}
              </MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
    ));
  }

  private maybeSortEnumMultiValues(contentItem: FormGenContentItem, enumValues: KeyTextString[], newValues: string[]): string[] {
    if (contentItem.metadata.enumMultiSortByValues) {
      newValues.sort((key1, key2) => enumValues.findIndex(kt => kt.key === key1) - enumValues.findIndex(kt => kt.key === key2));
    }
    return newValues;
  }

  private readEnumValue(contentItem: FormGenContentItem): { selectedValue: any, enumValues: KeyTextString[] } {
    let selectedValue = this.source.getValue(contentItem);
    if (contentItem.metadata.enumMulti && (selectedValue === undefined || selectedValue === null)) {
      this.source.setValue(contentItem, []);
      selectedValue = [];
    }
    const enumValues: KeyTextString[] = [];
    if (!contentItem.metadata.enumMulti && !contentItem.metadata.enumDefaultKey && !contentItem.metadata.enumDefaultKeyFirst) {
      enumValues.push($KTS("", "--"));
    }
    let values: KeyTextString[];
    if (contentItem.metadata.enumPresetsKeyType) {
      let presets = Presets.getInstance(new PresetsKey(contentItem.metadata.enumPresetsKeyType /*, TODO Set region */)).getListItems();
      if (contentItem.metadata.enumPresetsFilter) {
        presets = presets.filter(contentItem.metadata.enumPresetsFilter);
      }
      values = presets.map(preset => $KTS(preset.toKey(), preset.toString()));
    } else {
      values = contentItem.metadata.enumValues || [];
    }
    if (contentItem.metadata.enumFilter) {
      values = values?.filter(contentItem.metadata.enumFilter);
    }
    enumValues.push(...values);
    if (!selectedValue && contentItem.metadata.enumDefaultKey) {
      this.source.setValue(contentItem, contentItem.metadata.enumDefaultKey);
      selectedValue = contentItem.metadata.enumDefaultKey;
    }
    if (!selectedValue && contentItem.metadata.enumDefaultKeyFirst && enumValues.length > 0) {
      this.source.setValue(contentItem, enumValues[0].key);
      selectedValue = enumValues[0].key;
    }
    return {selectedValue, enumValues};
  }

  renderChecklist(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <FormGenChecklist
        disabled={this.isReadonly(contentItem)}
        contentItem={contentItem}
        index={index}
        source={this.source}
      />
    ));
  }

  renderDate(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <StyledBoxRow>
        <DesktopDatePicker
          // sx={{minWidth: PAGE_FRAGMENT_HALF_WIDTH}}
          value={this.source.getValue(contentItem)}
          onChange={date => this.source.setValue(contentItem, new Date(date))}/>
      </StyledBoxRow>
    ));
  }

  renderTime(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <StyledBoxRow>
        <DesktopTimePicker
          // sx={{minWidth: PAGE_FRAGMENT_HALF_WIDTH}}
          value={this.source.getValue(contentItem)}
          onChange={date => this.source.setValue(contentItem, new Date(date))}/>
      </StyledBoxRow>
    ));
  }

  renderDateTime(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <StyledBoxRow>
        <DesktopDateTimePicker
          // sx={{minWidth: PAGE_FRAGMENT_HALF_WIDTH}}
          value={this.source.getValue(contentItem)}
          onChange={date => this.source.setValue(contentItem, new Date(date))}/>
      </StyledBoxRow>
    ));
  }

  renderDatenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <StyledBoxRow>
        <DesktopDatePicker
          slotProps={{textField: {size: "small"}}}
          // sx={{minWidth: PAGE_FRAGMENT_HALF_WIDTH}}
          value={dayjs(this.source.getValue(contentItem))}
          onChange={date => this.source.setValue(contentItem, date.toDate().getTime())}/>
      </StyledBoxRow>
    ));
  }

  renderTimenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <StyledBoxRow>
        <DesktopTimePicker
          // sx={{minWidth: PAGE_FRAGMENT_HALF_WIDTH}}
          value={dayjs(this.source.getValue(contentItem))}
          onChange={date => this.source.setValue(contentItem, date.toDate().getTime())}/>
      </StyledBoxRow>
    ));
  }

  renderDateTimenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <StyledBoxRow>
        <DesktopDateTimePicker
          // sx={{minWidth: PAGE_FRAGMENT_HALF_WIDTH}}
          value={dayjs(this.source.getValue(contentItem))}
          onChange={date => this.source.setValue(contentItem, date.toDate().getTime())}/>
      </StyledBoxRow>
    ));
  }

  renderDateRange(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return this.renderInContainer(contentItem, (
      <FormGenDateRange
        disabled={this.isReadonly(contentItem)}
        contentItem={contentItem}
        index={index}
        source={this.source}/>
    ));
  }

  renderObject(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem,
      <Typography variant="h6"
                  style={{marginTop: PD_SM}}>{contentItem.metadata.name}</Typography>);
  }

  renderCustom(contentItem: FormGenContentItem, index: number): ReactElement {
    if (contentItem.metadata.styleFlags & FORMGEN_STYLE_FLAG_NO_CONTAINER) {
      return contentItem.metadata.customRenderer?.(contentItem.target, this.source.getValue(contentItem), value => this.source.setValue(contentItem, value), {
        path: this.path,
        mode: FormGenContainerMode.EDIT
      });
    }
    return this.renderInContainer(contentItem, (
      <Box style={{display: "flex", flexDirection: "column"}}>
        {contentItem.metadata.customRenderer?.(contentItem.target, this.source.getValue(contentItem), value => this.source.setValue(contentItem, value), {
          path: this.path,
          mode: FormGenContainerMode.EDIT
        })}
      </Box>
    ));
  }

  renderProfilePhoto(contentItem: FormGenContentItem, index: number): ReactElement {
    return this.renderInContainer(contentItem, (
      <Box style={{
        display: "flex", alignItems: "flex-start",
        // TODO Remove this
        // border: (this.source.getValue(contentItem) as string)?.includes("proxy") ? "2px solid red" : null,
      }}>
        <Box style={{display: "flex", flexDirection: "column", alignItems: "center", gap: PD_XSM}}>
          <ButtonBase onClick={() => this.onChangeProfilePhoto(contentItem)}>
            <Card>
              <img
                alt={contentItem.metadata.name}
                style={{
                  ...ProfilePhotoStyle(contentItem.metadata.imageOptions?.keepAspectRatio),
                  verticalAlign: "top",
                  objectFit: contentItem.metadata.profilePhotoObjectFit || "cover"
                }}
                src={this.source.getValue(contentItem) || contentItem.metadata.profilePhotoDefaultImageUrl || UserProfilePhoto()}/>
            </Card>
          </ButtonBase>
          <Typography variant="caption">Tap to change</Typography>
        </Box>
      </Box>
    ));
  }

  private onChangeProfilePhoto(contentItem: FormGenContentItem) {
    BaseApp.CONTEXT.showDialog(null, () => <FormGenProfilePhotoOptionsDialog
      contentItem={contentItem}
      source={this.source}/>);
  }

  renderIcon(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <FormGenIconSelect contentItem={contentItem} index={index} source={this.source}/>
    ));
  }

  renderColor(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <FormGenColorSelect contentItem={contentItem} index={index} source={this.source}/>
    ));
  }

  renderAutocomplete(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <FormGenAutocomplete
        disabled={this.isReadonly(contentItem)}
        contentItem={contentItem}
        index={index}
        source={this.source}
        showDescriptionInInput={contentItem.metadata.autocompleteShowDescriptionInInput}
        showDescriptionInMenu={contentItem.metadata.autocompleteShowDescriptionInMenu}
      />
    ));
  }

  renderCountrySelect(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <FormGenCountrySelect contentItem={contentItem} index={index} source={this.source}/>
    ));
  }

  private renderFileInternal(metadata: FormGenMetadata, getValue: () => string, setValue: (url: string) => void): ReactElement {
    switch (metadata.filePreviewType) {
      default:
      case "image":
        return this.renderFileInternalImage(metadata, getValue, setValue);
      case "audio":
        if (metadata.fileRecord) {
          return this.renderFileInternalRecordAudio(metadata, getValue, setValue);
        }
        return this.renderFileInternalAudio(metadata, getValue, setValue);
      case "video":
        break;
    }
    return null;
  }

  private renderFileInternalImage(metadata: FormGenMetadata, getValue: () => string, setValue: (url: string) => void) {
    let url = getValue();
    const previewSize = metadata.filePreviewSize || "small";
    let previewSizeStyle = null;
    switch (previewSize) {
      default:
      case "small":
        previewSizeStyle = PROFILE_PHOTO_STYLE_MD;
        break;
      case "medium":
        previewSizeStyle = PROFILE_PHOTO_STYLE_XXLG;
        break;
      case "large":
        previewSizeStyle = PROFILE_PHOTO_STYLE_JUMBO;
        break;
    }
    const mimeType = MimeTypeMap.getInstance().getMimeTypeFromExtension(MimeTypeMap.getFileExtensionFromUrl(url));
    const isImage = MimeTypeMap.isImage(mimeType);
    if (!isImage) {
      switch (mimeType) {
        case MimeTypeMap.MimeType_application_zip:
          url = iconMimeTypeApplicationZip;
          break;
      }
    }
    return <Box style={{display: "flex", alignItems: "flex-start"}}>
      <StyledBoxColumn style={{alignItems: "center"}}>
        <ButtonBase onClick={() => this.onChangeFile(metadata, getValue, setValue)}>
          <Card style={{
            ...previewSizeStyle,
            backgroundColor: lightGray,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            {url
              ? <img src={url} style={{
                width: !mimeType || isImage ? "100%" : "50%",
                aspectRatio: 1,
                objectFit: "contain",
                opacity: !mimeType || isImage ? 1 : 0.5,
              }}/>
              : <UploadFileOutlined style={{width: "50%", height: "50%", color: mediumGray, opacity: 0.5}}/>}
          </Card>
        </ButtonBase>
        <Typography variant="caption">Tap to change</Typography>
      </StyledBoxColumn>
    </Box>;
  }

  private renderFileInternalAudio(metadata: FormGenMetadata, getValue: () => string, setValue: (url: string) => void) {
    let url = getValue();
    return <Box style={{display: "flex", alignItems: "flex-start"}}>
      <StyledBoxColumn>
        <ButtonBase onClick={() => this.onChangeFile(metadata, getValue, setValue)} style={{height: 72}}>
          {url
            ? <StyledBoxRow style={{alignItems: "center"}}>
              <RestartAltOutlined style={{fontSize: 48, color: mediumGray}}/>
              <audio controls src={url}/>
            </StyledBoxRow>
            : <UploadFileOutlined style={{fontSize: 48, color: mediumGray, opacity: 0.5}}/>}
        </ButtonBase>
        <Typography variant="caption">Tap to change</Typography>
      </StyledBoxColumn>
    </Box>;
  }

  private renderFileInternalRecordAudio(metadata: FormGenMetadata, getValue: () => string, setValue: (url: string) => void) {
    let url = getValue();
    return <Box style={{display: "flex", alignItems: "flex-start"}}>
      <StyledBoxColumn>
        <ButtonBase onClick={() => this.onRecordAudioFile(metadata, getValue, setValue)} style={{height: 72}}>
          {url
            ? <StyledBoxRow style={{alignItems: "center"}}>
              <RestartAltOutlined style={{fontSize: 48, color: mediumGray}}/>
              <audio controls src={url}/>
            </StyledBoxRow>
            : <MicOutlined style={{fontSize: 48, color: mediumGray, opacity: 0.5}}/>}
        </ButtonBase>
        <Typography variant="caption">Tap to change</Typography>
      </StyledBoxColumn>
    </Box>;
  }

  private onChangeFile(metadata: FormGenMetadata, getValue: () => string, setValue: (url: string) => void) {
    BaseApp.CONTEXT.showDialog(null, () => this.showChangeFileOptions(metadata, getValue, setValue))
  }

  private showChangeFileOptions(metadata: FormGenMetadata, getValue: () => string, setValue: (url: string) => void) {
    const actions: Action[] = [
      new Action("Upload", () => {
        BaseApp.CONTEXT.showDialog(null, () => <StoredDataFileUploadFragment
          isDialog={true}
          mimeTypes={metadata.fileMimeTypes}
          select
          deferUpload={metadata.fileUploadOptions?.defer}
          storedDataSource={metadata.fileStoredDataSource || "files"}
          onSelectionChanged={(urls: string[]) => {
            setValue(urls[0]);
          }}/>);
      }),
      new Action("Remove", () => {
        setValue(null);
      }),
      new Action("Copy URL", () => {
        navigator.clipboard.writeText(getValue());
        BaseApp.CONTEXT.showToast("Copied.");
      }),
    ];
    const args = [getValue()];
    if (metadata.fileActions) {
      actions.push(...metadata.fileActions(args));
    }
    return <ActionsDialogContent actions={actions} args={args}/>;
  }

  private onRecordAudioFile(metadata: FormGenMetadata, getValue: () => string, setValue: (url: string) => void) {
    const id = BaseApp.CONTEXT.showDialog(null, () => <RecordAudioFragment
      onDone={url => {
        BaseApp.CONTEXT.hideDialog(id);
        setValue(url);
      }}/>);
  }

  renderFile(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem,
      this.renderFileInternal(contentItem.metadata, () => this.source.getValue(contentItem), url => this.source.setValue(contentItem, url))
    );
  }

  renderArrayString(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <FormGenArrayString contentItem={contentItem} index={index} source={this.source}/>
    ));
  }

  renderArrayAutocomplete(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderArrayFile(contentItem: FormGenContentItem, index: number) {
    const urls: string[] = [];
    const values: string[] = this.source.getValue(contentItem) as Array<string>;
    if (values?.length > 0) {
      urls.push(...(values.filter(value => value?.trim()?.length > 0)));
    }
    urls.push("");
    return this.renderInContainer(contentItem, (
      <StyledBoxRow>
        {urls.map((_, index) =>
          this.renderFileInternal(
            contentItem.metadata,
            () => this.source.getValue(contentItem)?.[index],
            url => {
              urls[index] = url;
              this.source.setValue(contentItem, urls);
            })
        )}
      </StyledBoxRow>
    ));
  }

  renderArrayObject(contentItem: FormGenContentItem, index: number) {
    return this.renderInContainer(contentItem, (
      <FormGenArrayObject contentItem={contentItem} index={index} source={this.source}/>
    ));
  }

  renderLatLng(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return this.renderInContainer(contentItem,
      <FormGenLatLng contentItem={contentItem} index={index} source={this.source}/>
    );
  }
}

class CompactFormGenLayout extends AbstractFormGenLayout {

  constructor(path: PathProps, source: FormGenLayoutSource) {
    super(path, FormGenLayoutType.COMPACT, source);
  }

  renderBoolean(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledHorizontalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <Switch
          disabled={this.isReadonly(contentItem)}
          size="small"
          style={{flexGrow: 1}}
          value={this.source.getValue(contentItem)}
          onChange={event => this.source.setValue(contentItem, event.target.checked)}
          id={contentItem.id}
        />
      </Box>
    </StyledHorizontalStack>
  }

  renderString(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledHorizontalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <TextField
          inputProps={{maxLength: contentItem.metadata.maxLength}}
          disabled={this.isReadonly(contentItem)}
          size="small"
          style={{width: SZ_LG, flexGrow: 1}}
          value={this.source.getValue(contentItem)}
          onChange={event => this.source.setValue(contentItem, formGenContentItemTransformText(contentItem, event.target.value))}
          id={contentItem.id} variant="outlined" required/>
      </Box>
    </StyledHorizontalStack>
  }

  renderText(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderRichText(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderRichContent(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderCurrency(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderCopyable(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderEmail(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderPasskey(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderNumber(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderEnum(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledVerticalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <FormControl variant="outlined">
          <Select id={contentItem.id}
                  disabled={this.isReadonly(contentItem)}
                  value={this.source.getValue(contentItem)}
                  onChange={event => this.source.setValue(contentItem, event.target.value)}>
            {[$KTS("", ""), ...contentItem.metadata.enumValues].map(value => <MenuItem
              value={value.key}>{value.text}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
    </StyledVerticalStack>;
  }

  renderChecklist(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderDate(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledVerticalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <DesktopDatePicker
          value={this.source.getValue(contentItem)}
          onChange={date => this.source.setValue(contentItem, new Date(date))}/>
      </Box>
    </StyledVerticalStack>;
  }

  renderTime(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledVerticalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <DesktopTimePicker
          value={this.source.getValue(contentItem)}
          onChange={date => this.source.setValue(contentItem, new Date(date))}/>
      </Box>
    </StyledVerticalStack>;
  }

  renderDateTime(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledVerticalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <DesktopDateTimePicker
          value={this.source.getValue(contentItem)}
          onChange={date => this.source.setValue(contentItem, new Date(date))}/>
      </Box>
    </StyledVerticalStack>;
  }

  renderDatenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledVerticalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <DesktopDatePicker
          value={new Date(this.source.getValue(contentItem))}
          onChange={date => this.source.setValue(contentItem, new Date(date).getTime())}/>
      </Box>
    </StyledVerticalStack>;
  }

  renderTimenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledVerticalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <DesktopTimePicker
          value={new Date(this.source.getValue(contentItem))}
          onChange={date => this.source.setValue(contentItem, new Date(date).getTime())}/>
      </Box>
    </StyledVerticalStack>;
  }

  renderDateTimenum(contentItem: FormGenContentItem, index: number): ReactElement {
    return <StyledVerticalStack label={contentItem.metadata.name}>
      <Box display="flex" flexDirection="row" style={{gap: PD_SM}}>
        <DesktopDateTimePicker
          value={new Date(this.source.getValue(contentItem))}
          onChange={date => this.source.setValue(contentItem, new Date(date).getTime())}/>
      </Box>
    </StyledVerticalStack>;
  }

  renderDateRange(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderObject(contentItem: FormGenContentItem, index: number) {
    return <Typography variant="h6" style={{marginTop: PD_SM}}>{contentItem.metadata.name}</Typography>;
  }

  renderCustom(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderProfilePhoto(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderIcon(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderColor(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderAutocomplete(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderCountrySelect(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderFile(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderArrayString(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderArrayAutocomplete(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderArrayFile(contentItem: FormGenContentItem, index: number) {
    return undefined;
  }

  renderArrayObject(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }

  renderLatLng(contentItem: FormGenContentItem, index: number): React.ReactElement {
    return undefined;
  }
}

export interface FormGenRendererFactory<T = any> {

  render(metadataType: T, source: FormGenLayoutSource, layout: FormGenLayout, contentItem: FormGenContentItem, index: number): ReactElement;
}

export class FormGenContainer extends React.Component<FormGenContainerProps, FormGenContainerState> implements FormGenLayoutSource {

  private static readonly sRendererFactories = new Map<string, FormGenRendererFactory>();

  static registerFactory(layoutType: FormGenLayoutType | string, factory: FormGenRendererFactory): void {
    FormGenContainer.sRendererFactories.set(layoutType, factory);
  }

  private static sAgent: FormGenAgent;

  static registerAgent(agent: FormGenAgent): void {
    FormGenContainer.sAgent = agent;
  }

  static hasAgent(): boolean {
    return Boolean(this.sAgent);
  }

  static getAgent(): FormGenAgent | undefined {
    return this.sAgent;
  }

  private layout: AbstractFormGenLayout;
  private layoutGap: number;

  private scheduledSave: any;

  constructor(props: FormGenContainerProps, context: any) {
    super(props, context);
    this.state = {
      _values: {},
    }
  }

  private readonly _key: string = md5_uuid();

  getKey(): string {
    return this._key;
  }

  getMode(): FormGenContainerMode {
    return this.props.mode || FormGenContainerMode.CREATE;
  }

  private async update(updateHidden?: boolean): Promise<void> {
    const contentItems = this.findContentItems(this.props.content);
    for (const item of contentItems) {
      if (item.metadata._asyncInit) {
        await item.metadata._asyncInit(item.metadata, item.target);
      }
    }
    const _values = this.state._values;
    contentItems.forEach(contentItem => _values[contentItem.id] = contentItem.target[contentItem.metadata._propertyKey]);
    this.setState({
      contentItems: contentItems,
      _values: _values,
    });
    if (this.props.mode === FormGenContainerMode.READ) {
      this.layout = new ReadFormGenLayout(this.props.path, this);
      this.layoutGap = PD_XLG;
    } else {
      switch (this.props.layout || "default") {
        default:
        case "default":
          this.layout = new DefaultFormGenLayout(this.props.path, this);
          this.layoutGap = PD_XLG;
          break;
        case "compact":
          this.layout = new CompactFormGenLayout(this.props.path, this);
          this.layoutGap = PD_XSM;
          break;
        case "custom":
          this.layout = this.props.customLayout(this.props.path, this);
          this.layoutGap = PD_XSM;
          break;
      }
    }
    if (updateHidden) {
      setTimeout(() => {
        const patch = {};
        this.state.contentItems?.forEach(contentItem => {
          this.patchContentItemsHidden(contentItem, this.state._values[contentItem.id], patch);
        });
        this.updateHidden(patch);
      }, 1);
    }
  }

  componentDidMount() {
    this.props.saveActionButtonProvider?.setActionButtonOnClick(() => this.doSave());
    // Assume no unsaved changes and no unfilled required items.
    this.props.saveActionButtonProvider?.setActionButtonEnabled(false);
    this.update(true);
  }

  componentDidUpdate(prevProps: Readonly<FormGenContainerProps>, prevState: Readonly<FormGenContainerState>, snapshot?: any) {
    if (prevProps.content !== this.props.content
      || prevProps.mode !== this.props.mode
      || prevProps.layout !== this.props.layout
      || prevProps.nestedObjectId !== this.props.nestedObjectId
      || prevState._hiddens !== this.state._hiddens) {
      this.update();
    }
    if (prevProps.multiStepIndex !== this.props.multiStepIndex) {
      this.update(true);
    }
    if (prevState.contentItems !== this.state.contentItems) {
      this.checkUnfilledRequiredItems();
    }
    if (prevState.hasUnsavedChanges !== this.state.hasUnsavedChanges || prevState.hasUnfilledRequiredItems !== this.state.hasUnfilledRequiredItems) {
      this.props.saveActionButtonProvider?.setActionButtonEnabled(this.state.hasUnsavedChanges && !this.state.hasUnfilledRequiredItems);
    }
  }

  save() {
    this.saveItems(this.state.contentItems);
  }

  private saveItems(contentItems: FormGenContentItem[]) {
    contentItems?.forEach(contentItem => {
      if (contentItem.metadata.type === "object") {
        this.saveItems(this.findContentItems(contentItem.target[contentItem.metadata._propertyKey]));
        return;
      }
      return this.saveItem(contentItem, this.state._values[contentItem.id]);
    })
  }

  private saveItem(contentItem: FormGenContentItem, value: any) {
    contentItem.target[contentItem.metadata._propertyKey] = value;
    this.scheduleSave();
  }

  private checkUnfilledRequiredItems(): boolean {
    if (this.state.contentItems) {
      let value: any;
      for (const item of this.state.contentItems) {
        if (item.hidden) {
          continue;
        }
        if ((item.metadata.required && item.parentRequired) && (!(value = item.target[item.metadata._propertyKey]) || (Array.isArray(value) && value.length <= 0))) {
          // A required item hasn't been filled yet.
          if (!this.state.hasUnfilledRequiredItems) {
            this.props.onContainerRequiredItemsFilledChanged?.(this, false);
            // this.props.saveActionButtonProvider?.setActionButtonEnabled(false);
          }
          this.setState({
            hasUnfilledRequiredItems: true,
          });
          return true;
        }
      }
    }
    return false;
  }

  private scheduleSave() {
    this.setState({
      hasUnsavedChanges: true,
    });
    if (this.checkUnfilledRequiredItems()) {
      return;
    }
    if (this.state.hasUnfilledRequiredItems) {
      this.props.onContainerRequiredItemsFilledChanged?.(this, true);
      // this.props.saveActionButtonProvider?.setActionButtonEnabled(true);
      this.setState({
        hasUnfilledRequiredItems: false,
      });
    }
    this.props.onContainerWillSave?.(this);
    if (this.props.autoSave) {
      clearTimeout(this.scheduledSave);
      this.scheduledSave = setTimeout(() => this.doSave(), this.props.saveDelay || 1000);
    }
  }

  private doSave(): void {
    this.props.onContainerSave?.(this, success => {
      this.setState({
        hasUnsavedChanges: !success,
      });
    });
  }

  // Uploads any 'deferred' properties (e.g. profile photos) that are set as 'blob:...' to cloud storage, and sets the returned url.
  static async maybeUploadItemBlobs(object: any): Promise<void> {
    const items = this.findContentItemsInternal(object);
    for (const item of items) {
      const value: any = item.target[item.metadata._propertyKey];
      if (item.metadata.type === "profile_photo") {
        const profilePhoto: string = value;
        if (profilePhoto?.startsWith("blob:")) {
          const blob = await fetch(profilePhoto).then(res => res.blob());
          const url = await FileUtil.uploadBlob(blob, item.metadata.profilePhotoStoredDataSource || "profile-photos", null);
          item.target[item.metadata._propertyKey] = url;
        }
      }
      // We must iterate over actual items in "value", as those are not found during the formgen scan of type metadata.
      if (Array.isArray(value)) {
        for (const item of value) {
          await this.maybeUploadItemBlobs(item);
        }
      }
    }
  }

  static getNestedObjectItems(object: any, parentKey: string, hiddens: any, showPropertyIds: string[], hidePropertyIds: string[], multiStepIndex: number | undefined): FormGenNestedObjectItem[] {
    return this.findContentItemsInternal(object, parentKey, false, true, showPropertyIds, hidePropertyIds, hiddens, multiStepIndex, true)
      .filter((item: FormGenContentItem) => item.metadata.type === "object")
      .map((item: FormGenContentItem) => ({
        id: item.id,
        propertyKey: item.metadata._propertyKey,
        text: item.metadata.name
      }));
  }

  private findContentItems(object: any): FormGenContentItem[] {
    const showPropertyIds = this.props.showPropertyIds;
    const hidePropertyIds = this.props.hidePropertyIds;
    const multiStepIndex: number | undefined = Boolean(this.props.multiStep) ? (this.props.multiStepIndex || 0) : undefined;
    if (this.props.nestedObjectId) { // nestedObjectId is set when we want to show only this property.
      const nestedObjectItems = FormGenContainer.getNestedObjectItems(object, this.getKey(), this.state._hiddens, showPropertyIds, hidePropertyIds, multiStepIndex);
      return FormGenContainer.findContentItemsInternal(object[nestedObjectItems.find(item => {
        const i1 = item.id.lastIndexOf("-");
        const i2 = this.props.nestedObjectId.lastIndexOf("-");
        // item ids includes the parent id as well, which is not known when setting the nested object id, so we check only the last fragment.
        return item.id.substring(i1 + 1) === this.props.nestedObjectId.substring(i2 + 1);
      })?.propertyKey], this.getKey(), false, true, this.state._hiddens, showPropertyIds, hidePropertyIds, multiStepIndex);
    }
    return FormGenContainer.findContentItemsInternal(object, this.getKey(), false, true, this.state._hiddens, showPropertyIds, hidePropertyIds, multiStepIndex);
  }

  private static findContentItemsInternal(object: any, parentKey: string = 'formgen-obj', parentHidden: boolean = false, parentRequired: boolean = true, hiddens: any = {}, showPropertyIds?: string[], hidePropertyIds?: string[], multiStepIndex?: number | undefined, norecursive?: boolean): FormGenContentItem[] {
    let contentItems: FormGenContentItem[] = [];
    if (!object) {
      return contentItems;
    }
    if (object instanceof Array) {
      const array: any[] = object;
      array.forEach((item, index) => contentItems.push(...this.findContentItemsInternal(item, `${parentKey}-${index}`, false, parentRequired, hiddens, showPropertyIds, hidePropertyIds, multiStepIndex)));
    } else {
      for (let propertyKey of Object.getOwnPropertyNames(object)) {
        if (propertyKey.startsWith("__formgen_")) {
          continue;
        }
        //@ts-ignore
        let metadata: FormGenMetadata = object[formGenMetadataKey(propertyKey)];
        if (!metadata) {
          continue;
        }
        if ((!showPropertyIds && !hidePropertyIds)
          || (metadata.id && showPropertyIds && showPropertyIds.includes(metadata.id))
          || (!metadata.id || !hidePropertyIds || !hidePropertyIds.includes(metadata.id))) {
          const hidden = Boolean(parentHidden || hiddens?.[md5(propertyKey)])
            || (multiStepIndex !== undefined && (multiStepIndex !== (metadata.multiStepIndex || 0)));
          contentItems.push({
            id: `${parentKey}-${md5(propertyKey)}`,
            target: object,
            metadata: metadata,
            hidden: hidden,
            parentRequired: parentRequired
          });
          if (metadata.type === "object" && !norecursive) {
            contentItems.push(...this.findContentItemsInternal(object[propertyKey], `${parentKey}-${propertyKey}`, hidden, metadata.required && parentRequired, hiddens, showPropertyIds, hidePropertyIds, undefined));
          }
        }
      }
    }
    contentItems.sort((a, b) => a.metadata.index - b.metadata.index);
    return contentItems;
  }

  componentWillUnmount() {
    this.props.saveActionButtonProvider?.setActionButtonOnClick(null);
    this.setState({
      contentItems: null,
    });
  }

  render() {
    if (!this.state.contentItems) {
      return null;
    }
    return <Box style={{
      opacity: !this.props.disabled ? 1 : 0.5,
      pointerEvents: !this.props.disabled ? "auto" : "none",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      padding: PD_MD,
      gap: this.layoutGap,
      //maxWidth: PAGE_FRAGMENT_WIDTH,
      ...this.props.style
    }}>
      {this.props.renderOptions?.showContentId
        ? <StyledBoxRow style={{alignItems: "center"}}>
          <Typography style={{fontWeight: 'bold'}}>ID</Typography>
          <Typography>{this.props.content?.id}</Typography>
          <Button onClick={() => navigator.clipboard.writeText("" + this.props.content?.id)}>Copy</Button>
        </StyledBoxRow>
        : null}
      {this.props.title ?
        <Typography style={{fontWeight: "bold", fontSize: "100%"}}>{this.props.title}</Typography> : null}
      {this.props.description ? <Typography variant="body2">{this.props.description}</Typography> : null}
      {this.state.contentItems.map((contentItem, index) => {
        return <>
          {contentItem.metadata.separator === "above" ?
            <div style={{marginTop: PD_SM, height: 1, borderBottom: DIVIDER}}/> : null}
          {contentItem.metadata.sectionTitle ?
            <Typography variant="h6"
                        style={{marginTop: PD_XLG}}>{contentItem.metadata.sectionTitle}</Typography> : null}
          {this.renderContentItem(contentItem, index)}
          {contentItem.metadata.separator === "below" ?
            <div style={{marginTop: PD_XSM, height: 1, borderBottom: DIVIDER}}/> : null}
        </>;
      })}
      {this.props.mode === FormGenContainerMode.EDIT && this.props.showSaveButton
        ? <StyledBoxRow>
          <Button disabled={!this.state.hasUnsavedChanges || this.state.hasUnfilledRequiredItems} variant="contained"
                  style={{flexGrow: 1, margin: "auto", maxWidth: PAGE_FRAGMENT_HALF_WIDTH}}
                  onClick={() => this.doSave()}>Save</Button>
        </StyledBoxRow>
        : null}
    </Box>
  }

  private renderContentItem(contentItem: FormGenContentItem, index: number) {
    if (contentItem.hidden || contentItem.metadata.hidden) {
      return null;
    }
    switch (contentItem.metadata.type) {
      case "none":
        return null;
      case "boolean":
        return this.layout.renderBoolean(contentItem, index);
      case "string":
        return this.layout.renderString(contentItem, index);
      case "text":
        return this.layout.renderText(contentItem, index);
      case "rich_text":
        return this.layout.renderRichText(contentItem, index);
      case "rich_content":
        return this.layout.renderRichContent(contentItem, index);
      case "currency":
        return this.layout.renderCurrency(contentItem, index);
      case "copyable":
        return this.layout.renderCopyable(contentItem, index);
      case "email":
        return this.layout.renderEmail(contentItem, index);
      case "passkey":
        return this.layout.renderPasskey(contentItem, index);
      case "number":
        return this.layout.renderNumber(contentItem, index);
      case "enum":
        return this.layout.renderEnum(contentItem, index);
      case "checklist":
        return this.layout.renderChecklist(contentItem, index);
      case "date":
        return this.layout.renderDate(contentItem, index);
      case "time":
        return this.layout.renderTime(contentItem, index);
      case "datetime":
        return this.layout.renderDateTime(contentItem, index);
      case "datenum":
        return this.layout.renderDatenum(contentItem, index);
      case "timenum":
        return this.layout.renderTimenum(contentItem, index);
      case "datetimenum":
        return this.layout.renderDateTimenum(contentItem, index);
      case "daterange":
        return this.layout.renderDateRange(contentItem, index);
      case "object":
        return this.layout.renderObject(contentItem, index);
      case "custom":
        return this.layout.renderCustom(contentItem, index);
      case "profile_photo":
        return this.layout.renderProfilePhoto(contentItem, index);
      case "icon":
        return this.layout.renderIcon(contentItem, index);
      case "color":
        return this.layout.renderColor(contentItem, index);
      case "autocomplete":
        return this.layout.renderAutocomplete(contentItem, index);
      case "country_select":
        return this.layout.renderCountrySelect(contentItem, index);
      case "file":
        return this.layout.renderFile(contentItem, index);
      case "array_string":
        return this.layout.renderArrayString(contentItem, index);
      case "array_autocomplete":
        return this.layout.renderArrayAutocomplete(contentItem, index);
      case "array_file":
        return this.layout.renderArrayFile(contentItem, index);
      case "array_object":
        return this.layout.renderArrayObject(contentItem, index);
      case "latlng":
        return this.layout.renderLatLng(contentItem, index);
    }
    const factory = FormGenContainer.sRendererFactories.get(this.layout.type);
    if (factory) {
      return factory.render(contentItem.metadata.type, this, this.layout, contentItem, index);
    }
    return null;
  }

  getValue(contentItem: FormGenContentItem) {
    return this.state._values[contentItem.id];
  }

  setValue(contentItem: FormGenContentItem, value: any) {
    let errorMessage: string;
    if (contentItem.metadata.onSetValue) {
      const result = contentItem.metadata.onSetValue(contentItem.target, value);
      errorMessage = (result as FormGenSetValueResult)?.errorMessage;
      this.setError(contentItem, errorMessage);
    }
    if (!errorMessage && contentItem.metadata.onSetTransformValue) {
      value = contentItem.metadata.onSetTransformValue(contentItem.target, value);
    }
    this.setState({
      _values: {
        ...this.state._values,
        [contentItem.id]: value,
      },
    });
    if (!errorMessage && !this.props.disableAutoWrite) {
      this.saveItem(contentItem, value);
      if (contentItem.metadata.onValueSet) {
        contentItem.metadata.onValueSet(contentItem.target, value, this.update.bind(this));
      }
    }
    const patch = {};
    this.patchContentItemsHidden(contentItem, value, patch);
    this.updateHidden(patch);
  }

  isHidden(contentItem: FormGenContentItem): boolean {
    return this.state._hiddens?.[contentItem.id];
  }

  updateHidden(patch: any) {
    const hiddens = {
      ...this.state._hiddens,
      ...patch,
    };
    this.setState({
      _hiddens: hiddens,
    });
  }

  getError(contentItem: FormGenContentItem) {
    return this.state._errors?.[contentItem.id];
  }

  setError(contentItem: FormGenContentItem, error: string | undefined | null) {
    const errors = {
      ...this.state._errors,
      [contentItem.id]: error,
    };
    this.setState({
      _errors: errors,
    });
    this.props.onContainerDidValidate?.(this, Object.getOwnPropertyNames(errors).findIndex(name => Boolean(errors[name])) < 0);
  }

  private patchContentItemsHidden(contentItem: FormGenContentItem, value: any, patch: any): void {
    this.state.contentItems?.forEach(item => {
      const hideOnPropertyId = item.metadata.hideOn?.propertyId;
      if (hideOnPropertyId && (hideOnPropertyId === contentItem.metadata.id)) {
        const hidden = item.metadata.hideOn.hide?.(contentItem.target, value);
        if (Boolean(hidden) !== this.isHidden(item)) {
          patch[item.id] = Boolean(patch[item.id]) || hidden;
        }
      }
    });
  }
}
